export enum ActivityNames {
  ANATOMY_LOCALIZATION = 'Anatomy Localization',
  CANNULATION_SCREW_PLACEMENT = 'Cannulation/Screw Placement',
  CLOSURE = 'Closure',
  DECOMPRESSION_ALIGNMENT = 'Decompression/Alignment',
  EXPOSURE = 'Exposure',
  CANNULATION = 'Cannulation',
  SCREW_PLACEMENT = 'Screw Placement',
  POST_CANNULATION = 'Post Cannulation Activity',
  ACCEPTED_REGISTRATION = 'Accepted Registration',
  REJECTED_REGISTRATION = 'Rejected Registration',
  INTRAOP_MEASUREMENT = 'Intraop Measurement',
}

export interface AlignmentMetric {
  category: string,
  display: boolean,
  id: string,
  label: string,
  unit: string,
  vertebra: string,
  value: number
}

export interface CaseActor {
  actorName: string;
}

export interface CannulationTime {
  vertebra: string;
  time_minutes: number;
}

export interface CaseAsset {
  category: string;
  signedUrl: string|null;
}

export interface CaseProcedure {
  procedureType: string;
  locusStart: string;
  locusEnd: string;
}

export interface CaseReport {
  id: string;
  date: Date;
  levels: string;
  name: string;
  hasActivities: boolean;
  hasRecording: boolean;
  isRevision: boolean;
  isNavigated: boolean;
  isStaged: boolean;
  stage: number | null;

  actors : CaseActor[];
  procedures : CaseProcedure[];

  alignment: {
    metric_sessions: MetricSession[];
  };
  cannulationTimes: CannulationTime[];
  pathLengths: PathLength[];
  segments: CaseReportSegment[];

  totals: CaseReportTotals;
  videos: CaseAsset[];
}

export interface CaseReportSegment {
  segmentType: string;
  normalizedStartMin: number; // Segment start time relative to "case start", in minutes.
  normalizedEndMin: number;   // Segment start time relative to "case start", in minutes.
  videoIsValid: boolean;    // True if the video segment is valid
  videoStartSec: number|null; // Segment video start time in seconds
  videoEndSec: number|null;   // Segment video start time in seconds
  segmentValue: string;
  signedUrl: string; // Video URL
}

export interface CaseReportSummary {
  id: string;
  date: Date;
  levels: string;
  name: string; // Display name for the case

  isRevision: boolean | null;
  isNavigated: boolean | null;
  isStaged: boolean | null;
  stage: number | null;

  actors : CaseActor[];
  procedures : CaseProcedure[];
}

/**
 * Timing totals for a case report. All times are expressed in minutes.
 *
 * @property has_split_cannulation_data - Flag indicating split cannulation data is available.
 *   If `true`, use `anatomy_localization_time`, `cannulation_time`, and `screw_placement_time`.
 *   If `false`, use `total_cannulation_screw_placement_time`.
 */
export interface CaseReportTotals {
  alignment_decompression_time: number;
  captured_operative_time: number;
  closure_time: number;
  exposure_time: number;
  post_cannulation_activity: number;
  total_cannulation_screw_placement_time: number;
  total_iom_time: number;
  total_navigated_time: number;
  anatomy_localization_time: number;
  cannulation_time: number;
  screw_placement_time: number;
  has_split_cannulation_data: boolean;
};

export interface MetricComparison {
  id: string,
  label: string,
  vertebra: string,
  preValue: number,
  preValueDesc: string,
  postValue?: number
  postValueDesc: string
  unit: string,
  valueDiffDesc: string
}

export enum MetricCategories {
  ALIGNMENT = 'alignment',
  ALIGNMENT_VALIDATION = 'alignment_comparison',
  CANNULATION = 'cannulation',
}

export interface MetricSession {
  sessionDescription: string;
  sessionName: string;
  sessionNameShorthand: string;
  metrics: AlignmentMetric[];
  meshAssetSignedUrl: string|null;
  validationAssetSignedUrl: string|null;
}

export enum MetricSessionTypes {
  PRE_ALIGN = 'Pre-Alignment',
  POST_OP_ALIGN = 'Post Op Alignment',
  INTRA_OP_ALIGN_PREFIX = 'Alignment Update',
}

export interface PathLength {
  vertebra: string;
  tool_name: string;
  path_length_meters: number;
}
